import { useRef, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import { Copy } from 'lucide-react';
import FlexCard from './FlexCard';

dayjs.extend(isToday);
dayjs.extend(isYesterday);

export default function ChatView({ messages = [], gptSuggestRef }) {
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const [showGoBottom, setShowGoBottom] = useState(false);
  const [manualScroll, setManualScroll] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const renderWithLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, idx) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={idx}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline break-words"
          >
            {part}
          </a>
        );
      } else {
        return <span key={idx}>{part}</span>;
      }
    });
  };
  

  const cardMap = {
    'PHETCHABURI THONGLOR': {
      title: 'PHETCHABURI THONGLOR',
      subtitle: 'ติดถนนเพชรบุรีตัดใหม่ หยุดทุกวันจันทร์\nเปิด 11:00 - 20:00 น.',
      imageUrl:
        'https://firebasestorage.googleapis.com/v0/b/refinery-desk-dashboard.firebasestorage.app/o/refinery-asset%2Fitem-shortcut%2F1669997734500-2jf3toEKlpqQleQik2NXxA47s85JQx0XnjAE0Oznu7ZchiSQui.jpg?alt=media',
      buttonLabel: 'ค้นหาเส้นทาง',
      buttonLink: 'https://rfn.clinic/w-thonglormaps',
    },
  };

  useEffect(() => {
    if (!manualScroll) {
      const timeout = setTimeout(() => {
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: 'auto' });
        }
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [messages, manualScroll]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const shouldShow = distanceFromBottom > 100;
    setShowGoBottom(shouldShow);
    setManualScroll(shouldShow);
  };

  const scrollToBottom = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      setManualScroll(false);
    }
  };

  const formatDateLabel = (date) => {
    if (dayjs(date).isToday()) return 'วันนี้';
    if (dayjs(date).isYesterday()) return 'เมื่อวาน';
    return dayjs(date).format('D MMM YYYY');
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  let lastDate = null;

  const buildGptPrompt = (messageText) => {
    return `จากที่ลูกค้าตอบมาว่า\n\n${messageText}\n\nควรตอบกลับ หรือแนะนำว่าอะไรดี โดยอ้างอิงจากบริบทที่คุยกับลูกค้าคนนี้ทั้งหมด`;
  };

  return (
    <div className="flex-1 relative h-full min-h-0 overflow-hidden">
      <div
        className="overflow-y-auto h-full min-h-0 space-y-4 py-4 pr-2"
        ref={containerRef}
        onScroll={handleScroll}
      >
        {messages.map((msg, index) => {
          const isMe = msg.from === 'me';
          const createdAt = msg.createdAt?.toDate ? dayjs(msg.createdAt.toDate()) : null;
          const showDateLabel =
            createdAt && (!lastDate || !createdAt.isSame(lastDate, 'day'));
          if (createdAt) lastDate = createdAt;

          const messageText = msg.customer || '—';

          return (
            <div key={index}>
              {showDateLabel && (
                <div className="flex justify-center my-4">
                  <div className="bg-gray-300 text-white text-xs px-3 py-1 rounded-full flex items-center space-x-1 shadow-sm">
                    <span>{formatDateLabel(createdAt)}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.1 1.02l-4.25 4.65a.75.75 0 01-1.1 0l-4.25-4.65a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              )}

              <div className={`flex items-end px-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                {!isMe && (
                  <img
                    src={msg.pictureUrl || '/default-avatar.png'}
                    alt="avatar"
                    className="w-10 h-10 rounded-full mr-2 flex-shrink-0"
                  />
                )}

                <div className={`max-w-[70%] ${isMe ? 'text-right' : 'text-left'}`}>
                  {!isMe && (
                    <div className="text-sm text-gray-500 font-medium mb-1 flex items-center gap-2">
                      {msg.displayName || 'Customer'}

                      {(msg.type === 'text' || (msg.type === 'image' && msg.imageUrl)) && (
                        <button
                          onClick={() =>
                            gptSuggestRef?.current?.askFromOutside(
                              msg.type === 'image'
                                ? `ลูกค้าส่งภาพเข้ามาตามนี้:\n${msg.imageUrl}\n\nช่วยดูภาพนี้แล้วแนะนำหน่อยว่าเราควรตอบกลับ หรืออธิบายอะไรเพิ่มเติมดี`
                                : buildGptPrompt(messageText)
                            )
                          }
                          className="text-xs text-gray-500 border border-gray-300 px-2 py-0.5 rounded hover:bg-gray-100"
                        >
                          Ask GPT
                        </button>
                      )}

                      {msg.type === 'text' && (
                        <button
                          onClick={() => handleCopy(messageText)}
                          title="คัดลอกข้อความ"
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {msg.type === 'text' && messageText.startsWith('[CARD]') ? (
                    (() => {
                      const cardKey = messageText.replace('[CARD]', '').trim();
                      const cardData = cardMap[cardKey];
                      return cardData ? (
                        <FlexCard {...cardData} />
                      ) : (
                        <div className="text-sm text-red-500">Card ไม่พบ: {cardKey}</div>
                      );
                    })()
                  ) : (
                    <div
                        className={`inline-block px-4 py-2 text-sm rounded-xl whitespace-pre-line text-left ${isMe ? 'bg-green-100 text-gray-900' : 'bg-gray-100 text-gray-900'
                        }`}
                    >
                      {msg.type === 'image' && msg.imageUrl ? (
                        <img
                          src={msg.imageUrl}
                          alt="uploaded"
                          className="rounded-md max-w-[200px] max-h-[200px] object-cover border cursor-pointer"
                          onClick={() => setPreviewImage(msg.imageUrl)}
                        />
                      ) : (
                       <div className="whitespace-pre-wrap break-words">
                          {renderWithLinks(messageText)}
                       </div>
                      )}
                    </div>
                  )}

                  <div
                    className={`text-xs text-gray-400 mt-1 flex ${isMe ? 'justify-end' : ''
                      } gap-1 items-center`}
                  >
                    {createdAt ? createdAt.format('HH.mm น.') : '—'}
                    {isMe && (
                      <span className="text-gray-400 text-xs">(Refinery Assistant Thonglor)</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {showGoBottom && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-gray-200/80 hover:bg-gray-300 text-gray-700 text-xs px-4 py-1 rounded-full shadow-sm z-40 transition"
        >
          Scroll to Bottom
        </button>
      )}

      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            alt="Full Preview"
            className="max-w-[75%] max-h-[75%] rounded shadow-lg"
          />
        </div>
      )}
    </div>
  );
}
